import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { AdminCounts, AdminDashboard, AdminFeatureId, ScheduledEvent } from "@tih/shared";
import { hasPermission, isRealEventPoster } from "@tih/shared";
import { adminFetchDashboard, adminFetchEvent, adminFetchEvents, adminFetchFeaturedEvent } from "../../api/client";
import { AdminIcon } from "../../components/admin/AdminIcon";
import { useAuth } from "../../context/AuthContext";
import {
  eventDisplayTitle,
  formatEventWhen,
  phaseBadgeClass,
  phaseLabel,
  rowPhase,
} from "../../lib/event-admin-utils";

const URGENT: {
  key: keyof AdminCounts;
  label: string;
  href: string;
  feature: AdminFeatureId;
}[] = [
  { key: "pendingTestimonials", label: "Pending testimonies", href: "/admin/testimonials", feature: "inbox" },
  { key: "pendingParticipation", label: "Pending participation", href: "/admin/participation", feature: "inbox" },
  { key: "pendingLiveComments", label: "Pending comments", href: "/admin/comments", feature: "inbox" },
  { key: "newRsvps", label: "New RSVPs", href: "/admin/rsvps", feature: "rsvps" },
  { key: "newContact", label: "Unread messages", href: "/admin/contact", feature: "inbox" },
  { key: "newPartners", label: "New partners", href: "/admin/partners", feature: "inbox" },
];

export function AdminDashboard() {
  const { user } = useAuth();
  const canTonight = hasPermission(user, "check_in")
    || hasPermission(user, "attendance")
    || hasPermission(user, "events")
    || hasPermission(user, "rsvps");
  const canContent = hasPermission(user, "content") || hasPermission(user, "events");
  const canPeople = hasPermission(user, "inbox") || hasPermission(user, "sms");

  const [dash, setDash] = useState<AdminDashboard | null>(null);
  const [featuredEvent, setFeaturedEvent] = useState<ScheduledEvent | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [d, featured, events] = await Promise.all([
          adminFetchDashboard(),
          adminFetchFeaturedEvent().catch(() => null),
          adminFetchEvents().catch(() => [] as ScheduledEvent[]),
        ]);
        if (cancelled) return;
        setDash(d);

        let ev: ScheduledEvent | null = null;
        if (featured) {
          ev = await adminFetchEvent(featured.id).catch(() => null);
        }
        if (!ev && events.length > 0) {
          const now = Date.now();
          ev =
            events.find((e) => rowPhase(e, now) === "ongoing") ??
            events.find((e) => rowPhase(e, now) === "upcoming") ??
            null;
        }
        if (!cancelled) setFeaturedEvent(ev);
      } catch (e) {
        if (!cancelled) setError(String((e as Error)?.message ?? e));
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);


  const counts = dash?.counts;
  const urgentChips = URGENT.filter(
    (t) => hasPermission(user, t.feature) && counts && counts[t.key] > 0,
  );
  const poster =
    featuredEvent && isRealEventPoster(featuredEvent.posterUrl)
      ? featuredEvent.posterUrl
      : null;
  const phase = featuredEvent ? rowPhase(featuredEvent) : null;

  return (
    <div className="admin-page">
      <header className="admin-page-header">
        <h1>Dashboard</h1>
        <p>Switchboard — tonight’s overnight and the three hubs. Site visibility lives under Settings → General.</p>
      </header>

      {error && <div className="form-error">{error}</div>}

      <section className="admin-hub-featured admin-card">
        {featuredEvent ? (
          <Link to="/admin/tonight" className="admin-hub-featured-link">
            {poster ? (
              <img src={poster} alt="" className="admin-hub-poster-thumb" />
            ) : (
              <div className="admin-hub-poster-thumb admin-hub-poster-thumb--empty" aria-hidden />
            )}
            <div>
              <p className="admin-hub-kicker">Next / featured overnight</p>
              <h2 className="admin-hub-poster-title">{eventDisplayTitle(featuredEvent)}</h2>
              <p className="admin-muted">{formatEventWhen(featuredEvent.startsAt)}</p>
              <div className="admin-hub-badge-row">
                {phase && (
                  <span className={phaseBadgeClass(phase)}>{phaseLabel(phase)}</span>
                )}
              </div>
            </div>
          </Link>
        ) : (
          <div className="admin-hub-empty">
            <p>No featured overnight yet.</p>
            {hasPermission(user, "events") ? (
              <Link to="/admin/events/new" className="btn btn-primary">
                Schedule an event
              </Link>
            ) : (
              <Link to="/admin/tonight" className="btn btn-ghost">
                Open Tonight
              </Link>
            )}
          </div>
        )}
      </section>

      {urgentChips.length > 0 && (
        <section className="admin-hub-urgent" aria-label="Needs attention">
          {urgentChips.map((chip) => (
            <Link key={chip.key} to={chip.href} className="admin-hub-urgent-chip">
              <strong>{counts![chip.key].toLocaleString()}</strong>
              <span>{chip.label}</span>
            </Link>
          ))}
        </section>
      )}

      <section className="admin-hub-entries" aria-label="Admin hubs">
        {canTonight && (
          <Link to="/admin/tonight" className="admin-hub-entry">
            <span className="admin-hub-entry-icon" aria-hidden>
              <AdminIcon name="tonight" size={28} />
            </span>
            <span className="admin-hub-entry-body">
              <strong>Tonight</strong>
              <span>Check-in, kiosk, attendance, stream, RSVPs</span>
            </span>
          </Link>
        )}
        {canContent && (
          <Link to="/admin/content-hub" className="admin-hub-entry">
            <span className="admin-hub-entry-icon" aria-hidden>
              <AdminIcon name="content" size={28} />
            </span>
            <span className="admin-hub-entry-body">
              <strong>Content</strong>
              <span>Videos, blog, gallery, pages, home video</span>
            </span>
          </Link>
        )}
        {canPeople && (
          <Link to="/admin/people" className="admin-hub-entry">
            <span className="admin-hub-entry-icon" aria-hidden>
              <AdminIcon name="people" size={28} />
            </span>
            <span className="admin-hub-entry-body">
              <strong>People</strong>
              <span>Testimonies, inbox, partners, SMS</span>
            </span>
          </Link>
        )}
      </section>

      <section className="admin-dashboard-section admin-hub-previews">
        <h2 className="admin-dashboard-heading">Public previews</h2>
        <div className="admin-dashboard-links admin-hub-preview-row">
          <a className="btn btn-ghost" href="/home" target="_blank" rel="noopener noreferrer">
            Full home
          </a>
          <a className="btn btn-ghost" href="/" target="_blank" rel="noopener noreferrer">
            Landing
          </a>
          <a
            className="btn btn-ghost"
            href="/preview/coming-soon"
            target="_blank"
            rel="noopener noreferrer"
          >
            Coming-soon
          </a>
        </div>
      </section>
    </div>
  );
}
